// src/pages/SingleListingPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { collection, getDocs, query, where } from 'firebase/firestore';

import './SingleListingPage.css';
import { db } from '../firebaseConfig';

// IMPORTANT: Define the same PREDEFINED_CONTACT_NUMBER here
const PREDEFINED_CONTACT_NUMBER = "+91 9569994536";
const PREDEFINED_CONTACT_NUMBER2 = "+91 7355694979";

const richTextRenderOptions = {
    renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => <p className="listing-full-paragraph">{children}</p>,
        [BLOCKS.HEADING_3]: (node, children) => <h3 className="listing-full-h3">{children}</h3>,
        [BLOCKS.HEADING_4]: (node, children) => <h4 className="listing-full-h4">{children}</h4>,
        [BLOCKS.UL_LIST]: (node, children) => <ul className="listing-full-ul">{children}</ul>,
        [BLOCKS.OL_LIST]: (node, children) => <ol className="listing-full-ol">{children}</ol>,
        [BLOCKS.LIST_ITEM]: (node, children) => <li className="listing-full-li">{children}</li>,
        [BLOCKS.HR]: () => <hr className="listing-full-hr" />,
        [BLOCKS.BLOCKQUOTE]: (node, children) => <blockquote className="listing-full-blockquote">{children}</blockquote>,
        [BLOCKS.EMBEDDED_ASSET]: (node) => {
            const { title, description, file } = node.data.target.fields || {};
            return (
                <img
                    src={file?.url || ''}
                    alt={description || title || "Embedded image"}
                    className="listing-full-embedded-image"
                />
            );
        },
    },
    renderMark: {
        [MARKS.BOLD]: text => <strong className="listing-full-bold">{text}</strong>,
        [MARKS.ITALIC]: text => <em className="listing-full-italic">{text}</em>,
    },
};

const SingleListingPage = () => {
    const { slug } = useParams();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allGalleryMedia, setAllGalleryMedia] = useState([]); // Stores all combined media
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0); // Index for the currently displayed main media

    // Memoized function to combine featured image, gallery images, and videos
    const combineMedia = useCallback((listingData) => {
        if (!listingData) return [];
        const mediaItems = [];
        const seenUrls = new Set(); // To prevent duplicate media

        // Add featuredImage first (maintains its position at the very beginning)
        if (listingData.featuredImage && listingData.featuredImage.file && listingData.featuredImage.file.url) {
            const url = listingData.featuredImage.file.url;
            if (!seenUrls.has(url)) {
                mediaItems.push({ ...listingData.featuredImage, type: 'image' });
                seenUrls.add(url);
            }
        }

        // --- MODIFICATION START ---
        // Add videos NEXT (so they appear after featured image, but before other gallery images)
        if (listingData.videos && Array.isArray(listingData.videos)) {
            listingData.videos.forEach(video => {
                if (video.file && video.file.url) {
                    const url = video.file.url;
                    if (!seenUrls.has(url)) {
                        mediaItems.push({ ...video, type: 'video' });
                        seenUrls.add(url);
                    }
                }
            });
        }

        // Add galleryImages LAST (they will now appear after videos)
        if (listingData.galleryImages && Array.isArray(listingData.galleryImages)) {
            listingData.galleryImages.forEach(img => {
                if (img.file && img.file.url) {
                    const url = img.file.url;
                    if (!seenUrls.has(url)) {
                        mediaItems.push({ ...img, type: 'image' });
                        seenUrls.add(url);
                    }
                }
            });
        }
        // --- MODIFICATION END ---

        return mediaItems;
    }, []);

    useEffect(() => {
        const fetchListingBySlug = async () => {
            setLoading(true);
            setError(null);
            setListing(null);
            setAllGalleryMedia([]); // Reset media
            setCurrentMediaIndex(0); // Reset index

            try {
                const listingsRef = collection(db, 'website_listings');
                const q = query(listingsRef, where('slug', '==', slug));
                const querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    const docData = querySnapshot.docs[0].data();
                    setListing(docData);

                    const combinedMedia = combineMedia(docData);
                    setAllGalleryMedia(combinedMedia);

                    let initialIdx = 0;
                    if (combinedMedia.length > 0) {
                        if (docData.featuredImage?.file?.url) {
                            const featIdx = combinedMedia.findIndex(m => m.file?.url === docData.featuredImage.file.url);
                            if (featIdx !== -1) initialIdx = featIdx;
                        }
                    }
                    setCurrentMediaIndex(initialIdx);
                } else {
                    setError("Listing not found.");
                }
            } catch (err) {
                console.error("Error fetching listing:", err);
                setError("Failed to load listing details. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchListingBySlug();
    }, [slug, combineMedia]);

    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        let date;
        if (dateValue.toDate && typeof dateValue.toDate === 'function') {
            date = dateValue.toDate();
        } else if (typeof dateValue === 'string') {
            date = new Date(dateValue);
        } else {
            return 'N/A';
        }
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    // --- Swipe Functionality ---
    const [touchStartX, setTouchStartX] = useState(0);

    const handleTouchStart = (e) => {
        setTouchStartX(e.touches[0].clientX);
    };

    const handleTouchMove = (e) => {
        const touchMoveX = e.touches[0].clientX;
        const diffX = touchMoveX - touchStartX;
        if (Math.abs(diffX) > 10 && Math.abs(e.touches[0].clientY - e.touches[0].clientY) < 20) {
            e.preventDefault();
        }
    };

    const handleTouchEnd = (e) => {
        if (allGalleryMedia.length <= 1) return;

        const touchEndX = e.changedTouches[0].clientX;
        const diffX = touchEndX - touchStartX;

        const swipeThreshold = 50; // Pixels
        if (Math.abs(diffX) > swipeThreshold) {
            if (diffX > 0) { // Swiped right
                setCurrentMediaIndex(prevIndex =>
                    (prevIndex - 1 + allGalleryMedia.length) % allGalleryMedia.length
                );
            } else { // Swiped left
                setCurrentMediaIndex(prevIndex =>
                    (prevIndex + 1) % allGalleryMedia.length
                );
            }
        }
        setTouchStartX(0); // Reset touch start position
    };

    const currentMainMedia = allGalleryMedia[currentMediaIndex];

    if (loading) return <div className="listings-empty">Loading listing details...</div>;
    if (error) return <div className="listings-empty error-message">{error}</div>;
    if (!listing) return <div className="listings-empty">Listing not found. (Check slug or Firestore data)</div>;

    const {
        listingName,
        rentPrice,
        rentUnit,
        location: propertyLocation,
        propertyType,
        description,
        amenities,
        availableFrom,
        urgencyMessage, // NEW: Destructure urgencyMessage
        genderPreference, // NEW: Destructure genderPreference
    } = listing;

    return (
        <div className="single-listing-container page-fade-in">
            <Link to="/listings" className="back-to-listings-link">&larr; Back to all listings</Link>

            <h1>{listingName}</h1>

            <div className="single-listing-info">
                <span className="single-listing-price">
                    ₹{rentPrice} {rentUnit || 'per month'}
                </span>
                <br/>
                <div className="single-listing-location">
                    {propertyLocation} ({propertyType}{genderPreference && ( <span className="gender-preference-value">, {genderPreference}</span> )})
                </div>
                <br/>
            {/* NEW DISPLAY: Custom Urgency Message */}
            {urgencyMessage && (
                <p className="urgency-message-details">
                    <span className="custom-message-text">{urgencyMessage}</span>
                </p>
            )}
            </div>
            
            

            {/* Main media display (image or video) with swipe functionality */}
            <div
                className="main-listing-image-container"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {currentMainMedia && currentMainMedia.file && currentMainMedia.file.url ? (
                    currentMainMedia.type === 'image' ? (
                        <img
                            src={currentMainMedia.file.url}
                            alt={currentMainMedia.description || listingName}
                            className="main-listing-image"
                        />
                    ) : ( // currentMainMedia.type === 'video'
                        <video controls className="main-listing-video">
                            <source src={currentMainMedia.file.url} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    )
                ) : (
                    <p>No media available</p>
                )}

                {allGalleryMedia.length > 1 && (
                    <>
                        <button
                            className="main-media-nav-button main-media-prev-button"
                            onClick={() => setCurrentMediaIndex(prevIndex => (prevIndex - 1 + allGalleryMedia.length) % allGalleryMedia.length)}
                            aria-label="Previous media"
                        >
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        <button
                            className="main-media-nav-button main-media-next-button"
                            onClick={() => setCurrentMediaIndex(prevIndex => (prevIndex + 1) % allGalleryMedia.length)}
                            aria-label="Next media"
                        >
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </>
                )}
            </div>

            {/* Thumbnail gallery - now displays both images and videos */}
            {(allGalleryMedia && allGalleryMedia.length > 0) && (
                <div className="single-listing-gallery">
                    {allGalleryMedia.map((media, index) => (
                        <div
                            key={media.file?.url || `thumb-${index}`}
                            onClick={() => setCurrentMediaIndex(index)}
                            className={`thumbnail-item ${index === currentMediaIndex ? 'active-thumbnail' : ''}`}
                        >
                            {media.type === 'image' ? (
                                <img
                                    src={media.file?.url || ''}
                                    alt={media.description || `Gallery image ${index + 1}`}
                                />
                            ) : ( // media.type === 'video'
                                <video muted preload="metadata">
                                    <source src={media.file?.url || ''} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                            <span className="media-type-icon">
                                {media.type === 'video' ? '▶️' : '🖼️'}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div className="single-listing-section">
                <h3>Property Description</h3>
                {description && (typeof description === 'string' ? (
                    <p className="listing-full-paragraph">{description}</p>
                ) : (
                    documentToReactComponents(description, richTextRenderOptions)
                ))}
                <p>
                    <strong>Available From:</strong> {formatDate(availableFrom)}
                </p>
            </div>

            <div className="single-listing-section">
                <h3>Details & Amenities</h3>
                {amenities && amenities.length > 0 ? (
                    <div className="amenities-grid">
                        {amenities.map((amenity, index) => (
                            <div key={index} className="amenity-item">
                                <span>{amenity}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p>No amenities listed.</p>
                )}
            </div>

            <div className="single-listing-section contact-info-sls">
                <p>
                    <strong>Contact:</strong> <a href={`tel:${PREDEFINED_CONTACT_NUMBER}`}>{PREDEFINED_CONTACT_NUMBER}</a> / <a href={`tel:${PREDEFINED_CONTACT_NUMBER2}`}>{PREDEFINED_CONTACT_NUMBER2}</a>
                </p>
            </div>

            <Link to="/listings" className="back-to-listings-link bottom-link">&larr; Back to all listings</Link>
        </div>
    );
};

export default SingleListingPage;